import { FormEvent, useState } from 'react';
import { api } from '../lib/api';
import { formatCLP } from '../lib/format';
import { useDocumentMeta } from '../lib/seo';

const STATUS_LABELS: Record<string, { label: string; description: string; step: number }> = {
  pending: { label: 'Pendiente de pago', description: 'Estamos esperando confirmación del pago.', step: 1 },
  paid: { label: 'Pago recibido', description: 'Tu pago entró. Estamos preparando el pedido.', step: 2 },
  preparing: { label: 'En preparación', description: 'Estamos armando tu pedido en bodega.', step: 2 },
  shipped: { label: 'Enviado', description: 'Tu pedido salió en despacho. Tienes el código de tracking abajo.', step: 3 },
  delivered: { label: 'Entregado', description: 'Tu pedido fue entregado. ¡Gracias!', step: 4 },
  cancelled: { label: 'Cancelado', description: 'Este pedido fue cancelado.', step: 0 },
  refunded: { label: 'Reembolsado', description: 'El pago fue devuelto.', step: 0 }
};

type TrackedOrder = {
  orderNumber: number;
  status: string;
  createdAt: string;
  updatedAt: string;
  total: number;
  shippingAddress: string;
  shippingCity: string;
  trackingNumber: string;
  trackingProvider: string;
  items: Array<{ name: string; sku: string; color: string; size: string; quantity: number; total: number }>;
};

export function TrackingPage({ navigate }: { navigate: (path: string) => void }) {
  useDocumentMeta({ title: 'Seguimiento de pedido · Run More Chile', noindex: true });
  const [orderNumber, setOrderNumber] = useState('');
  const [email, setEmail] = useState('');
  const [order, setOrder] = useState<TrackedOrder | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function search(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setOrder(null);
    setLoading(true);
    try {
      const res = await api.trackOrder(orderNumber.trim(), email.trim());
      setOrder(res.order);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No encontramos ese pedido');
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="static-page">
      <button className="back-button" onClick={() => navigate('/')}>Volver a tienda</button>
      <header className="static-page-header">
        <p className="eyebrow">Seguimiento</p>
        <h1>¿Dónde está mi pedido?</h1>
        <p className="static-page-intro">Ingresa el número de pedido y el email con que compraste para ver el estado actualizado.</p>
      </header>

      <form className="tracking-form" onSubmit={search}>
        <label className="field">
          <span>Número de pedido</span>
          <input value={orderNumber} onChange={(e) => setOrderNumber(e.target.value)} placeholder="Ej: 1015" required />
        </label>
        <label className="field">
          <span>Email</span>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="tu@correo.cl" required />
        </label>
        <button className="primary-button" disabled={loading}>{loading ? 'Buscando...' : 'Buscar pedido'}</button>
      </form>

      {error && <p className="form-message tone-error">{error}</p>}

      {order && (
        <article className="tracking-result">
          <div className="tracking-head">
            <h2>Pedido #{order.orderNumber}</h2>
            <span className={`order-pill pill-${order.status}`}>{STATUS_LABELS[order.status]?.label || order.status}</span>
          </div>
          <p className="muted">{STATUS_LABELS[order.status]?.description}</p>

          <div className="tracking-steps">
            {[
              { id: 1, label: 'Pago' },
              { id: 2, label: 'Preparación' },
              { id: 3, label: 'Despacho' },
              { id: 4, label: 'Entregado' }
            ].map((s) => {
              const currentStep = STATUS_LABELS[order.status]?.step || 0;
              const reached = currentStep >= s.id;
              return (
                <div key={s.id} className={`tracking-step ${reached ? 'done' : ''}`}>
                  <span>{s.id}</span>
                  <small>{s.label}</small>
                </div>
              );
            })}
          </div>

          {order.trackingNumber && (
            <p className="tracking-code">
              Código de seguimiento: <strong>{order.trackingNumber}</strong>
              {order.trackingProvider && <span className="muted"> · {order.trackingProvider}</span>}
            </p>
          )}

          <h3 style={{ marginTop: 24 }}>Productos</h3>
          <ul className="tracking-items">
            {order.items.map((item, i) => (
              <li key={i}>
                <span><strong>{item.name}</strong> {[item.color, item.size].filter(Boolean).length > 0 && <small>({[item.color, item.size].filter(Boolean).join(' / ')})</small>}</span>
                <span>x{item.quantity}</span>
                <span>{formatCLP(item.total)}</span>
              </li>
            ))}
          </ul>

          <div className="tracking-footer">
            <div>
              <small className="muted">Envío</small>
              <p>{order.shippingAddress}, {order.shippingCity}</p>
            </div>
            <div>
              <small className="muted">Total</small>
              <p><strong>{formatCLP(order.total)}</strong></p>
            </div>
          </div>
        </article>
      )}
    </main>
  );
}
